import { z } from 'zod';
// ============= Node Types =============
/**
 * Extended node types for all diagram categories.
 * Includes C4 Model types, infrastructure, and domain modeling.
 */
export const ExtendedNodeType = z.enum([
    // Original types
    'deployment',
    'codeBlock',
    'storage',
    'queue',
    'external',
    'database',
    'cache',
    'api',
    'frontend',
    'service',
    // C4 Model types
    'person',
    'softwareSystem',
    'container',
    'component',
    // Infrastructure types
    'server',
    'loadBalancer',
    'cdn',
    'firewall',
    'gateway',
    // Domain modeling types
    'entity',
    // Flow types
    'decision',
    'process',
    'start',
    'end',
    // Security types
    'trustBoundary',
    'dataStore',
    'externalEntity',
    // Issue #414: Call graph node types for multi-handle support
    'function',
    'method',
    'arrow',
    'constructor',
    'entry',
    'critical',
    'hotspot',
    'callGraphNode',
    // Issue #500/#565: Import graph node types
    'importGraphNode',
    'importGroupNode',
]);
// ============= Edge Types =============
/**
 * Extended edge types for various diagram connections.
 */
export const ExtendedEdgeType = z.enum([
    'default',
    'http',
    'async',
    'database',
    'file',
    // Additional types
    'sync',
    'bidirectional',
    'dependency',
    'inheritance',
    'composition',
    'aggregation',
    'uses',
    'publishes',
    'subscribes',
    'reads',
    'writes',
    'calls',
]);
// ============= C4 Model Levels =============
/**
 * C4 Model abstraction levels for software architecture diagrams.
 */
export const C4Level = z.enum([
    'context', // Level 1: System Context
    'container', // Level 2: Container
    'component', // Level 3: Component
    'code', // Level 4: Code
]);
// ============= Diagram Categories =============
/**
 * Categories of diagrams that can be generated.
 */
export const DiagramCategory = z.enum([
    'architecture',
    'infrastructure',
    'data',
    'behavior',
    'domain',
    'security',
    'operations',
]);
// ============= Specific Diagram Types =============
/**
 * Specific diagram types from the prompt library.
 */
export const SpecificDiagramType = z.enum([
    // Architecture (Phase 1)
    'databaseSchema',
    'pipelineDiagram',
    'apiMap',
    'dependencyGraph',
    // Behavior (Phase 2)
    'callGraph',
    // Domain & System (Phase 3)
    'systemContext',
    'deploymentPipeline',
    'monitoringDashboard',
    // Issue #330: E2E User Interaction Flow
    'e2eFlow',
    // Cached import graph from AST analysis
    'importGraph',
    // Issue #863 (epic #831): FCG/BCG promoted from e2eFlow metadata to first-class diagram rows
    'frontendCallGraph',
    'backendCallGraph',
]);
// ============= Code Evidence (Issue #175) =============
/**
 * Code evidence supporting a diagram element.
 * Used to show users the actual code that led to element detection.
 */
export const CodeEvidenceSchema = z.object({
    /** File path relative to repository root */
    filePath: z.string(),
    /** Starting line number (1-indexed) */
    lineStart: z.number().int().positive(),
    /** Ending line number (1-indexed) */
    lineEnd: z.number().int().positive(),
    /** The actual code snippet (optional - can be loaded lazily from file) */
    snippet: z.string().optional(),
    /** What pattern/keyword triggered this detection */
    matchedPattern: z.string().optional(),
    /** Confidence level: 0-1 */
    confidence: z.number().min(0).max(1).optional(),
});
// ============= Diagram Metadata =============
/**
 * Metadata associated with a diagram.
 */
export const DiagramMetadataSchema = z.object({
    // Diagram classification
    diagramType: SpecificDiagramType.optional(),
    category: DiagramCategory.optional(),
    c4Level: C4Level.optional(),
    // Display information
    title: z.string(),
    description: z.string().optional(),
    version: z.string().optional(),
    // Source tracking
    sourceFiles: z.array(z.string()).optional(),
    generatedAt: z.date().optional(),
    generatedBy: z.enum(['manual', 'regex', 'ast', 'ai']).optional(),
    // Issue #177: Code evidence for diagram elements
    sourceEvidence: z.array(CodeEvidenceSchema).optional(),
    // Layout preferences
    layoutDirection: z.enum(['TB', 'LR', 'BT', 'RL']).default('TB'),
    layoutAlgorithm: z.enum(['dagre', 'elk', 'force', 'radial', 'tree']).default('dagre'),
});
// ============= Unified Diagram Node =============
/**
 * Unified node type for the diagram data model.
 * Compatible with both Mermaid and React Flow renderers.
 */
export const UnifiedDiagramNodeSchema = z.object({
    id: z.string(),
    type: ExtendedNodeType,
    label: z.string(),
    description: z.string().optional(),
    // Position (for React Flow)
    position: z.object({
        x: z.number(),
        y: z.number(),
    }),
    // Dimensions (optional)
    width: z.number().optional(),
    height: z.number().optional(),
    // Styling
    style: z.object({
        backgroundColor: z.string().optional(),
        borderColor: z.string().optional(),
        textColor: z.string().optional(),
        borderWidth: z.number().optional(),
        borderStyle: z.enum(['solid', 'dashed', 'dotted']).optional(),
        shape: z.enum(['rectangle', 'rounded', 'diamond', 'circle', 'cylinder', 'hexagon']).optional(),
    }).optional(),
    // Additional data
    data: z.record(z.unknown()).optional(),
    // Grouping
    parentId: z.string().optional(),
    // C4 specific
    technology: z.string().optional(),
    tags: z.array(z.string()).optional(),
    // Code evidence (Issue #175)
    /** Code evidence supporting this node's existence in the diagram */
    evidence: z.array(CodeEvidenceSchema).optional(),
});
// ============= Unified Diagram Edge =============
/**
 * Unified edge type for the diagram data model.
 * Compatible with both Mermaid and React Flow renderers.
 */
export const UnifiedDiagramEdgeSchema = z.object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    // Labels
    label: z.string().optional(),
    sourceLabel: z.string().optional(),
    targetLabel: z.string().optional(),
    // Type and appearance
    type: ExtendedEdgeType.default('default'),
    animated: z.boolean().default(false),
    // Styling
    style: z.object({
        strokeColor: z.string().optional(),
        strokeWidth: z.number().optional(),
        strokeDasharray: z.string().optional(),
        arrowType: z.enum(['arrow', 'none', 'diamond', 'circle']).optional(),
    }).optional(),
    // Additional data
    data: z.record(z.unknown()).optional(),
});
// ============= Unified Diagram Data =============
/**
 * The complete unified diagram data model.
 * This is the canonical format that both Mermaid and React Flow renderers consume.
 */
export const UnifiedDiagramDataSchema = z.object({
    metadata: DiagramMetadataSchema,
    nodes: z.array(UnifiedDiagramNodeSchema),
    edges: z.array(UnifiedDiagramEdgeSchema),
});
// ============= Render Mode =============
/**
 * Available rendering modes for diagrams.
 */
export const RenderMode = z.enum(['interactive']);
// ============= Export Format =============
/**
 * Export formats for diagrams.
 */
export const ExtendedExportFormat = z.enum(['svg', 'png', 'json']);
// ============= Color Palette =============
/**
 * Semantic color categories for diagram nodes.
 */
export const NodeColorCategory = z.enum([
    'primary', // Blue - main elements, normal flow
    'success', // Green - success states, outputs, trusted
    'warning', // Yellow - data stores, queues, caution
    'danger', // Red - errors, untrusted, threats
    'info', // Purple - external services, integrations
    'neutral', // Gray - third-party, secondary
]);
// ============= Helper Functions =============
/**
 * Create a new unified diagram with defaults.
 */
export function createUnifiedDiagram(title, options) {
    return {
        metadata: {
            title,
            layoutDirection: 'TB',
            layoutAlgorithm: 'dagre',
            ...options,
        },
        nodes: [],
        edges: [],
    };
}
/**
 * Create a new diagram node with defaults.
 */
export function createDiagramNode(id, label, type = 'service', position = { x: 0, y: 0 }) {
    return {
        id,
        type,
        label,
        position,
    };
}
/**
 * Create a new diagram edge with defaults.
 */
export function createDiagramEdge(id, source, target, label) {
    return {
        id,
        source,
        target,
        label,
        type: 'default',
        animated: false,
    };
}
//# sourceMappingURL=diagram.js.map